import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import FrameGrabber from './FrameGrabber';
import { updateTrip } from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { httpsify } from '../utils/url';

// Resolve the best still image for an asset (poster for clips, developed thumb
// for RAW, presigned original for plain photos). Returns a URL or ''.
function useAssetImage(asset) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let on = true;
    if (!asset) {
      setUrl('');
      return undefined;
    }
    if (asset.thumbUrl) {
      setUrl(httpsify(asset.thumbUrl));
    } else if (asset.s3Key && !asset.isRaw && asset.kind === 'photo') {
      getAssetPlaybackUrl(asset.s3Key).then((u) => on && setUrl(u)).catch(() => on && setUrl(''));
    } else {
      setUrl('');
    }
    return () => {
      on = false;
    };
  }, [asset]);
  return url;
}

const findAsset = (assets, id) => (id ? assets.find((a) => a.id === id) : null);

const DEFAULT_FRAME = { x: 0, y: 0, zoom: 1 };
const clampFrame = (f) => ({
  x: Math.max(-60, Math.min(60, Number(f.x) || 0)),
  y: Math.max(-60, Math.min(60, Number(f.y) || 0)),
  zoom: Math.max(1, Math.min(4, Number(f.zoom) || 1)),
});

// One magazine slot. Shows a filled cover (click → open) or a dotted empty
// placeholder (click → pick, when editing). When editing, the framing tool lets
// you drag to reposition and zoom the crop precisely (saved as slot.frame).
function FeatureSlot({ slot, assets, tripId, editing, className, label, onPick, onClear, onOpenPhoto, onFrame }) {
  const navigate = useNavigate();
  const asset = findAsset(assets, slot?.assetId);
  // Grid display prefers the lightweight thumbnail/poster; a full-resolution
  // frame (slot.frameImg) is only used as a fallback for display and is reserved
  // for the click-to-fullscreen view so the spread stays fast to load.
  const live = useAssetImage(asset);
  const img = live || (slot?.frameImg ? httpsify(slot.frameImg) : '');
  const kind = slot?.kind || asset?.kind;
  const gone = slot && !asset; // referenced media was deleted

  const [framing, setFraming] = useState(false);
  const [liveFrame, setLiveFrame] = useState(null);
  const boxRef = useRef(null);
  const savedFrame = slot?.frame ? clampFrame(slot.frame) : DEFAULT_FRAME;
  const frame = liveFrame || savedFrame;

  // Keep local framing state in sync if the saved value changes externally.
  useEffect(() => {
    if (!framing) setLiveFrame(null);
  }, [framing, slot?.frame?.x, slot?.frame?.y, slot?.frame?.zoom]);

  if (!slot || gone) {
    // In view mode an empty slot is invisible (keeps the spread clean); only
    // surface the dashed "add" placeholder while editing.
    if (!editing) return null;
    return (
      <button
        type="button"
        className={`cs-slot cs-slot-empty ${className || ''}`}
        onClick={onPick}
        title="Add a clip, frame or photo"
      >
        <span className="cs-slot-plus">＋</span>
        <span className="cs-slot-hint">{gone ? 'media removed — pick again' : `Add ${label || 'media'}`}</span>
      </button>
    );
  }

  const open = async () => {
    if (editing) return; // clicks are for framing/tools while editing
    // A grabbed frame is already a full-resolution still; a photo asset opens at
    // its highest available resolution (presigned original for plain photos,
    // else the largest derived). Videos go to the player.
    if (slot?.frameImg) {
      onOpenPhoto(httpsify(slot.frameImg));
      return;
    }
    if (kind === 'photo') {
      let hi = img;
      if (asset?.displayUrl) {
        // Graded full-res display (RED negatives keep their look here, not baked
        // into the master).
        hi = httpsify(asset.displayUrl);
      } else if (asset?.s3Key && !asset?.isRaw) {
        try {
          hi = await getAssetPlaybackUrl(asset.s3Key);
        } catch (_e) {
          /* fall back to the display image */
        }
      } else if (asset?.derivedUrl || asset?.derived?.url) {
        hi = httpsify(asset.derivedUrl || asset.derived.url);
      }
      onOpenPhoto(hi || img);
      return;
    }
    navigate(`/watch/asset/${tripId}/${asset.id}`);
  };

  const commit = (f) => {
    const c = clampFrame(f);
    setLiveFrame(c);
    if (onFrame) onFrame(c.x === 0 && c.y === 0 && c.zoom === 1 ? null : c);
  };

  // Drag to pan: convert pointer delta to a percentage of the slot box.
  const onPanDown = (e) => {
    if (!framing) return;
    e.preventDefault();
    e.stopPropagation();
    const box = boxRef.current;
    if (!box) return;
    const rect = box.getBoundingClientRect();
    const start = { mx: e.clientX, my: e.clientY, x: frame.x, y: frame.y };
    const move = (ev) => {
      const dx = ((ev.clientX - start.mx) / rect.width) * 100;
      const dy = ((ev.clientY - start.my) / rect.height) * 100;
      commit({ ...frame, x: start.x + dx, y: start.y + dy });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const transform = `translate(${frame.x}%, ${frame.y}%) scale(${frame.zoom})`;

  return (
    <div ref={boxRef} className={`cs-slot cs-slot-filled ${className || ''} ${framing ? 'is-framing' : ''}`}>
      <button
        type="button"
        className="cs-slot-cover"
        onClick={open}
        onPointerDown={onPanDown}
        style={{ cursor: framing ? 'move' : editing ? 'default' : 'pointer' }}
      >
        {img ? (
          <img
            className="cs-slot-img"
            src={img}
            alt=""
            draggable={false}
            loading="lazy"
            decoding="async"
            style={{ transform }}
          />
        ) : (
          <span className="cs-slot-ph">{kind === 'photo' ? '🖼' : '▶'}</span>
        )}
        {kind !== 'photo' && img && !framing && <span className="cs-slot-play">▶</span>}
        {slot.frameImg && !framing && <span className="cs-slot-tag">frame</span>}
      </button>
      {editing && (
        <div className="cs-slot-tools">
          {img && (
            <button
              type="button"
              className={`cs-btn cs-btn-sm ${framing ? 'cs-btn-primary' : 'cs-btn-ghost'}`}
              onClick={() => setFraming((v) => !v)}
              title="Reframe — drag to move, slider to zoom"
            >
              {framing ? '✓ Frame' : '⤢ Frame'}
            </button>
          )}
          {!framing && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onPick}>Replace</button>}
          {!framing && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onClear}>Remove</button>}
        </div>
      )}
      {editing && framing && img && (
        <div className="cs-slot-frame-ctrl" onPointerDown={(e) => e.stopPropagation()}>
          <span className="cs-slot-frame-ico" title="Zoom">🔍</span>
          <input
            type="range"
            min="1"
            max="4"
            step="0.02"
            value={frame.zoom}
            onChange={(e) => commit({ ...frame, zoom: Number(e.target.value) })}
          />
          <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => commit(DEFAULT_FRAME)}>Reset</button>
        </div>
      )}
    </div>
  );
}

// Small thumbnail used inside the media picker.
function PickerTile({ asset, onPoster, onFrame, onPhoto }) {
  const img = useAssetImage(asset);
  const isVideo = asset.kind !== 'photo';
  const isRed = !!asset.isRawVideo;
  const proxyReady = asset.proxy?.status === 'ready' && !!asset.proxyHlsUrl;
  // RED clips can be grabbed once their preview proxy is decoded; MP4s anytime.
  const canGrab = isRed ? proxyReady : !!asset.s3Key;
  return (
    <div className="cs-pick-tile">
      <div className="cs-pick-thumb" style={img ? { backgroundImage: `url(${img})` } : {}}>
        {!img && <span>{isVideo ? '▶' : '🖼'}</span>}
      </div>
      <div className="cs-pick-name" title={asset.name}>{asset.name}</div>
      {isVideo ? (
        <div className="cs-pick-actions">
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onPoster} disabled={!img} title={img ? '' : 'Poster still rendering'}>Poster</button>
          <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={onFrame} disabled={!canGrab} title={canGrab ? 'Pick an exact frame' : isRed ? 'RED clip: decoding preview…' : 'No source'}>Grab frame</button>
        </div>
      ) : (
        <div className="cs-pick-actions">
          <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={onPhoto}>Use photo</button>
        </div>
      )}
    </div>
  );
}

function MediaPicker({ assets, tripId, onClose, onChoose }) {
  const [frameFor, setFrameFor] = useState(null); // asset being frame-grabbed
  const videos = assets.filter((a) => a.kind !== 'photo');
  const photos = assets.filter((a) => a.kind === 'photo');
  return (
    <Modal title="Choose media for this spot" onClose={onClose} maxWidth={920}>
      {assets.length === 0 ? (
        <div className="cs-empty">No media in this trip yet. Upload footage first.</div>
      ) : (
        <>
          {videos.length > 0 && <div className="cs-meta-sub" style={{ marginTop: 0 }}>Clips</div>}
          <div className="cs-pick-grid">
            {videos.map((a) => (
              <PickerTile
                key={a.id}
                asset={a}
                onPoster={() => onChoose({ assetId: a.id, kind: 'video', source: 'asset' })}
                onFrame={() => setFrameFor(a)}
              />
            ))}
          </div>
          {photos.length > 0 && <div className="cs-meta-sub">Photos</div>}
          <div className="cs-pick-grid">
            {photos.map((a) => (
              <PickerTile
                key={a.id}
                asset={a}
                onPhoto={() => onChoose({ assetId: a.id, kind: 'photo', source: 'asset' })}
              />
            ))}
          </div>
        </>
      )}
      {frameFor && (
        <FrameGrabber
          asset={frameFor}
          tripId={tripId}
          onClose={() => setFrameFor(null)}
          onSaved={(assetId) => {
            // The grabbed frame is now a real trip photo asset; place it here.
            onChoose({ assetId, kind: 'photo', source: 'asset' });
            setFrameFor(null);
          }}
        />
      )}
    </Modal>
  );
}

// Editorial "magazine" feature board for a trip: a large hero plus side tiles
// and an optional carousel strip. Each slot can be filled with a clip poster, a
// grabbed frame, or a photo, and the arrangement persists on the trip.
export default function TripFeature({ trip, tripId, assets, onOpenPhoto, editing = false, onEditingChange }) {
  const [pickPath, setPickPath] = useState(null); // {area, idx}
  const [saving, setSaving] = useState(false);
  const scroller = useRef(null);
  const setEditing = onEditingChange || (() => {});

  const layout = useMemo(() => {
    const l = trip.layout || {};
    return {
      hero: l.hero || null,
      tiles: Array.isArray(l.tiles) ? l.tiles.slice(0, 4) : [null, null, null, null],
      carousel: Array.isArray(l.carousel) ? l.carousel : [],
    };
  }, [trip.layout]);

  const tilesPadded = [
    layout.tiles[0] || null,
    layout.tiles[1] || null,
    layout.tiles[2] || null,
    layout.tiles[3] || null,
  ];
  const hasAny = layout.hero || tilesPadded.some(Boolean) || layout.carousel.length > 0;

  const save = async (next) => {
    setSaving(true);
    try {
      await updateTrip(tripId, { layout: next });
    } finally {
      setSaving(false);
    }
  };

  const choose = (value) => {
    if (!pickPath) return;
    const next = { ...layout, tiles: [...tilesPadded], carousel: [...layout.carousel] };
    if (pickPath.area === 'hero') next.hero = value;
    else if (pickPath.area === 'tiles') next.tiles[pickPath.idx] = value;
    else if (pickPath.area === 'carousel') {
      if (pickPath.idx >= next.carousel.length) next.carousel = [...next.carousel, value];
      else next.carousel[pickPath.idx] = value;
    }
    setPickPath(null);
    save(next);
  };

  const clear = (area, idx) => {
    const next = { ...layout, tiles: [...tilesPadded], carousel: [...layout.carousel] };
    if (area === 'hero') next.hero = null;
    else if (area === 'tiles') next.tiles[idx] = null;
    else if (area === 'carousel') next.carousel = next.carousel.filter((_, i) => i !== idx);
    save(next);
  };

  // Persist a slot's pan/zoom framing. Debounced so dragging/zooming stays smooth
  // (the slot renders its own live preview while we throttle Firestore writes).
  const frameTimer = useRef(null);
  const setFrame = (area, idx, frame) => {
    if (frameTimer.current) clearTimeout(frameTimer.current);
    frameTimer.current = setTimeout(() => {
      const cur = trip.layout || {};
      const next = {
        hero: cur.hero || null,
        tiles: Array.isArray(cur.tiles) ? cur.tiles.slice(0, 4) : [null, null, null, null],
        carousel: Array.isArray(cur.carousel) ? [...cur.carousel] : [],
      };
      const apply = (s) => (s ? { ...s, frame: frame || null } : s);
      if (area === 'hero') next.hero = apply(next.hero);
      else if (area === 'tiles') next.tiles[idx] = apply(next.tiles[idx]);
      else if (area === 'carousel') next.carousel[idx] = apply(next.carousel[idx]);
      save(next);
    }, 450);
  };
  useEffect(() => () => { if (frameTimer.current) clearTimeout(frameTimer.current); }, []);

  // Nothing arranged and not editing: render nothing (the "Edit magazine" entry
  // lives in the trip header) to keep the page minimal.
  if (!hasAny && !editing) return null;

  const slotProps = (area, idx, label) => ({
    slot: area === 'hero' ? layout.hero : area === 'tiles' ? tilesPadded[idx] : layout.carousel[idx],
    assets,
    tripId,
    editing,
    label,
    onPick: () => setPickPath({ area, idx }),
    onClear: () => clear(area, idx),
    onFrame: (frame) => setFrame(area, idx, frame),
    onOpenPhoto,
  });

  return (
    <section className="cs-mag-wrap">
      {editing && (
        <div className="cs-mag-editbar">
          <span className="cs-help" style={{ margin: 0 }}>Click a spot to fill it · use ⤢ Frame to drag &amp; zoom what’s in view.</span>
          {saving && <span className="cs-mag-saving">saving…</span>}
        </div>
      )}

      <div className="cs-mag">
        <FeatureSlot className="cs-mag-hero" {...slotProps('hero', 0, 'a hero shot')} />
        {tilesPadded.map((_, i) => (
          <FeatureSlot key={i} className="cs-mag-tile" {...slotProps('tiles', i, 'a shot')} />
        ))}
      </div>

      {/* Extra shots live in an editable strip; kept out of the default view so
          the spread + highlights fit on screen without scrolling. */}
      {editing && (
        <div className="cs-mag-carousel-wrap">
          <div className="cs-mag-editbar" style={{ marginBottom: 6 }}>
            <span className="cs-help" style={{ margin: 0 }}>Extra shots (shown only here, while editing)</span>
          </div>
          <div className="cs-mag-carousel" ref={scroller}>
            {layout.carousel.map((_, i) => (
              <FeatureSlot key={i} className="cs-mag-cslot" {...slotProps('carousel', i, 'a shot')} />
            ))}
            <FeatureSlot
              className="cs-mag-cslot"
              slot={null}
              assets={assets}
              editing
              label="to the carousel"
              onPick={() => setPickPath({ area: 'carousel', idx: layout.carousel.length })}
              onClear={() => {}}
              onOpenPhoto={onOpenPhoto}
            />
          </div>
        </div>
      )}

      {pickPath && (
        <MediaPicker assets={assets} tripId={tripId} onClose={() => setPickPath(null)} onChoose={choose} />
      )}
    </section>
  );
}
